import { useState } from 'react';
import {
  Body,
  Button,
  Caption,
  Card,
  Dialog,
  DialogFooter,
  Label,
  Stack,
  TextLink,
  type ButtonSize,
  type ButtonVariant,
} from '@leanin/ui';
import { useTranslations } from '@/i18n/translations';
import { RouterLinkAs } from '@/lib/router-link';
import { withReturnPath } from '@/lib/return-path';
import { clearPendingJoinGroup } from '@/components/onboarding/pending-join-group';

// The search param that marks a return from sign-up / sign-in as "I came
// back to register". The detail page uses it to put the registration action
// in front of the member; it never registers on its own.
export const REGISTER_INTENT_PARAM = 'register';

export function registerReturnPath(eventId: string): string {
  return `/events/${eventId}?${REGISTER_INTENT_PARAM}=1`;
}

type Props = {
  readonly eventId: string;
  readonly eventTitle: string;
  /** The event's date and time as the surface shows it, e.g. "Nov 5 · 12:00 – 1:00 PM PST". */
  readonly eventWhen: string;
  readonly variant: Extract<ButtonVariant, 'primary' | 'secondary'>;
  /** The trigger's control tier. Callers set it explicitly — the detail
   *  page's hero action and a directory card's footer button sit at
   *  different sizes, so there's no safe shared default. */
  readonly size: Extract<ButtonSize, 'sm' | 'md'>;
};

// A signed-out visitor's Register button. Registration needs a Lean In
// account, so it opens a prompt that sends them to sign-up (or sign-in) and
// back to this exact event once they're through verification and onboarding.
// Dismissing it leaves them where they were.
export function RegisterNeedsAccountDialog({ eventId, eventTitle, eventWhen, variant, size }: Props) {
  const t = useTranslations('Events');
  const [open, setOpen] = useState(false);
  const next = registerReturnPath(eventId);
  // A pending "Sign up to Join" intent left behind by an earlier, abandoned
  // Circle or Network click would outrank `next` at /auth/finishing and land
  // the new member on that group instead of this event.
  const chooseAuth = () => clearPendingJoinGroup();

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title={t('public.register.title')}
      trigger={
        <Button
          variant={variant}
          size={size}
          aria-label={t('public.register.ctaAria', { title: eventTitle })}
        >
          {t('public.register.cta')}
        </Button>
      }
    >
      <Stack gap="lg">
        <Body>{t('public.register.body')}</Body>
        <Card pad="compact">
          <Stack gap="xs">
            <Label>{eventTitle}</Label>
            <Caption>{eventWhen}</Caption>
          </Stack>
        </Card>
      </Stack>
      <DialogFooter justify="space-between">
        <Body>
          {t.rich('public.register.signIn', {
            link: (chunks) => (
              <TextLink
                href={withReturnPath('/sign-in', next)}
                linkAs={RouterLinkAs}
                onClick={chooseAuth}
              >
                {chunks}
              </TextLink>
            ),
          })}
        </Body>
        <Button
          variant="primary"
          size="md"
          href={withReturnPath('/get-started', next)}
          linkAs={RouterLinkAs}
          onClick={chooseAuth}
        >
          {t('public.register.createAccount')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
